'use client';

import cn from 'classnames';
import { type ReactNode } from 'react';

import { useDecisions, useDeviceType } from '@libs/common-next';
import { SddAddressDrawer } from '@libs/shared-ui';

import { useProduct } from '@/entities/product';
import { useAutoshipCustomerStatus } from '@/features/autoship-customer-status';
import { FrequencyCalculatorDrawer } from '@/features/frequency-calculator-drawer';
import { FulfilmentSelectionDrawer } from '@/features/fulfilment-selection';

import { useOptimizelyPurchaseCTAObserver } from './model';
import {
    AddToCartButton,
    DeliveryDetails,
    DeliveryOptions,
    FrequencyOptions,
    LiveTreatPoints,
    PersonalizationVariants,
    PurchaseCTA,
    PurchaseDisclaimer,
    PurchaseForm,
    PurchaseOptions,
} from './ui';

import './PurchasePanel.scss';

export type PurchasePanelProps = {
    renderSlot: ReactNode;
    isLivePetInStoreOnly: boolean;
};

export const PurchasePanel = ({ renderSlot, isLivePetInStoreOnly }: PurchasePanelProps) => {
    const autoshipFlag = useDecisions('ff_autoship');
    const mobileStickyATCFlag = useDecisions('m_sticky_cta');
    const { deviceType } = useDeviceType();
    const { product, isLoading, tailored, isError } = useProduct();
    const isMobileStickyPermanentCTA =
        mobileStickyATCFlag === 'var1' && deviceType === 'mobile' && !product?.personalization;
    const isMobileStickyScrollCTA =
        mobileStickyATCFlag === 'var2' && deviceType === 'mobile' && !product?.personalization;
    const { hasPassed, sentinelRef } = useOptimizelyPurchaseCTAObserver(isMobileStickyScrollCTA);
    const { isExistingAutoshipCustomer } = useAutoshipCustomerStatus();

    if (isLivePetInStoreOnly) {
        return (
            <PurchaseForm>
                {renderSlot}
                <DeliveryDetails />
            </PurchaseForm>
        );
    }

    return (
        <PurchaseForm>
            <PersonalizationVariants />
            {renderSlot}
            <PurchaseOptions />
            <DeliveryOptions />
            <DeliveryDetails />
            <PurchaseDisclaimer />
            <div
                className={cn(
                    {
                        'add-to-cart-button-optimizely__sticky':
                            isMobileStickyPermanentCTA || (isMobileStickyScrollCTA && hasPassed),
                    },
                    'purchase-form__footer'
                )}
            >
                <PurchaseCTA />
            </div>
            {isMobileStickyScrollCTA && <div ref={sentinelRef} aria-hidden="true" />}
            <LiveTreatPoints />

            <SddAddressDrawer
                isDeliveryAvailable={Boolean(tailored?.variants?.[product?.sku ?? '']?.SDD?.doorDashValidationSuccess)}
                isLoading={isLoading}
                isError={isError}
            />
            <FrequencyCalculatorDrawer />

            {isExistingAutoshipCustomer && autoshipFlag === 'crawl' && (
                <FulfilmentSelectionDrawer
                    addToCartSlot={<AddToCartButton />}
                    selectFrequencySlot={<FrequencyOptions />}
                />
            )}
        </PurchaseForm>
    );
};
